// GestMob Offline Storage - IndexedDB wrapper for offline sync
const DB_NAME = 'gestmob-offline';
const DB_VERSION = 1;

// Store names
const STORES = {
  PENDING_ACTIONS: 'pending_actions',
  CACHED_DATA: 'cached_data',
  SYNC_META: 'sync_meta'
};

class OfflineStorage {
  constructor() {
    this.db = null;
    this.isReady = false;
    this.readyPromise = this.init();
  }

  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('[OfflineStorage] Failed to open database');
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.isReady = true;
        console.log('[OfflineStorage] Database ready');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Store for pending actions (scans, creates, updates)
        if (!db.objectStoreNames.contains(STORES.PENDING_ACTIONS)) {
          const pendingStore = db.createObjectStore(STORES.PENDING_ACTIONS, { 
            keyPath: 'id', 
            autoIncrement: true 
          });
          pendingStore.createIndex('type', 'type', { unique: false });
          pendingStore.createIndex('timestamp', 'timestamp', { unique: false });
          pendingStore.createIndex('synced', 'synced', { unique: false });
        }

        // Store for cached API data
        if (!db.objectStoreNames.contains(STORES.CACHED_DATA)) {
          const cacheStore = db.createObjectStore(STORES.CACHED_DATA, { 
            keyPath: 'key' 
          });
          cacheStore.createIndex('endpoint', 'endpoint', { unique: false });
          cacheStore.createIndex('cachedAt', 'cachedAt', { unique: false });
        }

        // Store for sync metadata
        if (!db.objectStoreNames.contains(STORES.SYNC_META)) {
          db.createObjectStore(STORES.SYNC_META, { keyPath: 'key' });
        }

        console.log('[OfflineStorage] Database schema created');
      };
    });
  }

  async waitForReady() {
    if (this.isReady) return this.db;
    return this.readyPromise;
  }

  // ==================== PENDING ACTIONS ====================

  async addPendingAction(action) {
    await this.waitForReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.PENDING_ACTIONS], 'readwrite');
      const store = transaction.objectStore(STORES.PENDING_ACTIONS);
      
      const pendingAction = {
        ...action,
        timestamp: Date.now(),
        synced: false,
        retryCount: 0
      };

      const request = store.add(pendingAction);
      request.onsuccess = () => {
        console.log('[OfflineStorage] Pending action added:', action.type);
        resolve(request.result);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getPendingActions() {
    await this.waitForReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.PENDING_ACTIONS], 'readonly');
      const store = transaction.objectStore(STORES.PENDING_ACTIONS);
      const request = store.getAll();
      
      request.onsuccess = () => {
        // Filter for non-synced actions
        const allActions = request.result || [];
        const pendingActions = allActions.filter(action => !action.synced);
        resolve(pendingActions);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async markActionSynced(id) {
    await this.waitForReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.PENDING_ACTIONS], 'readwrite');
      const store = transaction.objectStore(STORES.PENDING_ACTIONS);
      const request = store.get(id);
      
      request.onsuccess = () => {
        const action = request.result;
        if (action) {
          action.synced = true;
          action.syncedAt = Date.now();
          store.put(action);
        }
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  async deletePendingAction(id) {
    await this.waitForReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.PENDING_ACTIONS], 'readwrite');
      const store = transaction.objectStore(STORES.PENDING_ACTIONS);
      const request = store.delete(id);
      
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async clearSyncedActions() {
    await this.waitForReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.PENDING_ACTIONS], 'readwrite');
      const store = transaction.objectStore(STORES.PENDING_ACTIONS);
      const request = store.getAll();
      
      request.onsuccess = async () => {
        const allActions = request.result || [];
        const syncedActions = allActions.filter(a => a.synced);
        for (const action of syncedActions) {
          store.delete(action.id);
        }
        console.log(`[OfflineStorage] Cleared ${syncedActions.length} synced actions`);
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getPendingActionsCount() {
    const actions = await this.getPendingActions();
    return actions.filter(a => !a.synced).length;
  }

  // ==================== CACHED DATA ====================

  async cacheData(endpoint, data, options = {}) {
    await this.waitForReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.CACHED_DATA], 'readwrite');
      const store = transaction.objectStore(STORES.CACHED_DATA);
      
      const cacheEntry = {
        key: endpoint,
        endpoint,
        data,
        cachedAt: Date.now(),
        expiresAt: options.ttl ? Date.now() + options.ttl : null,
        source: options.source || 'api'
      };

      const request = store.put(cacheEntry);
      request.onsuccess = () => {
        console.log('[OfflineStorage] Data cached:', endpoint);
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getCachedData(endpoint) {
    await this.waitForReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.CACHED_DATA], 'readonly');
      const store = transaction.objectStore(STORES.CACHED_DATA);
      const request = store.get(endpoint);
      
      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          // Check expiration
          if (result.expiresAt && Date.now() > result.expiresAt) {
            console.log('[OfflineStorage] Cache expired:', endpoint);
            resolve(null);
            return;
          }
          resolve(result);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getAllCachedData() {
    await this.waitForReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.CACHED_DATA], 'readonly');
      const store = transaction.objectStore(STORES.CACHED_DATA);
      const request = store.getAll();
      
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async clearCache() {
    await this.waitForReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.CACHED_DATA], 'readwrite');
      const store = transaction.objectStore(STORES.CACHED_DATA);
      const request = store.clear();
      
      request.onsuccess = () => {
        console.log('[OfflineStorage] Cache cleared');
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  // ==================== SYNC METADATA ====================

  async setLastSync(key, timestamp = Date.now()) {
    await this.waitForReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.SYNC_META], 'readwrite');
      const store = transaction.objectStore(STORES.SYNC_META);
      const request = store.put({ key, timestamp });
      
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getLastSync(key) {
    await this.waitForReady();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORES.SYNC_META], 'readonly');
      const store = transaction.objectStore(STORES.SYNC_META);
      const request = store.get(key);
      
      request.onsuccess = () => {
        resolve(request.result?.timestamp || null);
      };
      request.onerror = () => reject(request.error);
    });
  }
}

// Singleton instance
export const offlineStorage = new OfflineStorage();

// Helper to format cache age
export function formatCacheAge(timestamp) {
  if (!timestamp) return 'Jamais';
  
  const now = Date.now();
  const diff = now - timestamp;
  
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) return `Il y a ${days} jour${days > 1 ? 's' : ''}`;
  if (hours > 0) return `Il y a ${hours} heure${hours > 1 ? 's' : ''}`;
  if (minutes > 0) return `Il y a ${minutes} minute${minutes > 1 ? 's' : ''}`;
  return 'À l\'instant';
}

export default offlineStorage;
